import { ProductVisibilityTypes } from "@utils/enums";

import { ECommerceProduct } from "@components/Product/ECommerce/eCommerce.types";

import { FullProductApiModel } from "lib/@types/service/product/api/product.api.model";

import { brandMapper } from "./brand.mapper";
import { priceMapper } from "./price.mapper";

export const isClickAndCollectAvailable = (
    visibilityType: ProductVisibilityTypes | string,
) => {
    switch (visibilityType) {
        case ProductVisibilityTypes.Collect:
        case ProductVisibilityTypes.DeliveryAndCollect:
        case ProductVisibilityTypes.CollectAndExpressDelivery:
        case ProductVisibilityTypes.DeliveryAndExpressDeliveryAndCollect:
            return true;
        default:
            return false;
    }
};

export const isHomeDeliveryAvailable = (
    visibilityType: ProductVisibilityTypes | string,
) => {
    switch (visibilityType) {
        case ProductVisibilityTypes.Delivery:
        case ProductVisibilityTypes.DeliveryAndCollect:
        case ProductVisibilityTypes.DeliveryAndExpressDeliveryAndCollect:
            return true;
        default:
            return false;
    }
};

const productMapper = (entity: FullProductApiModel): ECommerceProduct => {
    const visibilityType = (entity?.productVisibilityType ||
        ProductVisibilityTypes.Default) as ProductVisibilityTypes;

    const superCategory =
        entity.superCategories && entity.superCategories.length
            ? entity.superCategories[0]
            : null;

    const product: ECommerceProduct = {
        sku: entity.sku,
        ean: entity.ean,
        baseStore: entity.baseStore,
        supplierCode: entity.supplierCode,
        supplierProductCode: entity.supplierProductCode,
        followupCategoryId: entity.followupCategoryId,
        baseProduct: entity.baseProduct,
        energyLabel: entity.energyLabel,
        starStatus: entity.starStatus,
        codeMDM: entity.codeMDM,
        codeSAP: entity.codeSAP,
        calculatorCampaignId: entity.calculatorCampaignId,
        favvCode: entity.favvCode,
        signalword: entity.signalword,
        sdsUrl: entity.sdsUrl,
        dopUrl: entity.dopUrl,
        url: entity.url,
        superCategoryUrl: entity.superCategoryUrl,
        localizedUrls: entity.localizedUrls,
        conversionUnit: entity.conversionUnit,
        description: entity.description,
        name: entity.name,
        productTitle: entity.productTitle,
        superCategoryName: entity.superCategoryName,
        dsvDeliveryTime: entity.dsvDeliveryTime,
        roundingValue: entity.roundingValue,
        minimumOrderQuantity: entity.minimumOrderQuantity,
        conversionFactor: entity.conversionFactor,
        rating: entity.rating,
        doubleBonusPoints: entity.doubleBonusPoints,
        updatedSinceLastRequest: entity.updatedSinceLastRequest,
        assemblyProductRequired: entity.assemblyProductRequired,
        saleableSeparately: entity.saleableSeparately,
        notPurchasableByCustomerPickUpInStore:
            entity.notPurchasableByCustomerPickUpInStore,
        notPurchasableByCustomerHomeDelivery:
            entity.notPurchasableByCustomerHomeDelivery,
        giftCardArticle: entity.giftCardArticle,
        giftCardArticleType: entity.giftCardArticleType,
        giftCardArticleLanguage: entity.giftCardArticleLanguage,
        boxArticle: entity.boxArticle,
        boxLogisticInformation: entity.boxLogisticInformation,
        visibleNavigation: entity.visibleNavigation,
        recupel: entity.recupel,
        forSaleDSV: entity.forSaleDSV,
        bebat: entity.bebat,
        displayPrice: entity.displayPrice,
        showConvertedPriceAsMainPrice: entity.showConvertedPriceAsMainPrice,
        stockAtSupplier: entity.stockAtSupplier,
        locale: entity.locale,
        references: entity.references,
        brand: brandMapper(entity.brand),
        clickAndCollectSupported: entity.clickAndCollectSupported,
        shipToStoreSupported: entity.shipToStoreSupported,
        homeDeliverySupported: entity.homeDeliverySupported,
        shipToStorePurchasable: entity.shipToStorePurchasable,
        purchaseBlockade: entity.purchaseBlockade,
        clickAndCollectDeliveryTimeInHours:
            entity.clickAndCollectDeliveryTimeInHours,
        shipToStoreDeliveryTimeInHours: entity.shipToStoreDeliveryTimeInHours,
        homeDeliveryTimeInHours: entity.homeDeliveryTimeInHours,
        homeDeliveryTime: entity.homeDeliveryTime,
        homeDeliveryTimeUnit: entity.homeDeliveryTimeUnit,
        availableIn10Stores: entity.availableIn10Stores,
        everyDayLowPrice: entity.everyDayLowPrice,
        variants: entity.variants,
        deliveryTypes: entity.deliveryTypes,
        superCategories: entity.superCategories,
        classifyingCategories: entity.classifyingCategories,
        accessoryParents: entity.accessoryParents,
        montageParents: entity.montageParents,
        labels: entity.labels,
        additionalEans: entity.additionalEans,
        safetyIcons: entity.safetyIcons,
        keywords: entity.keywords,
        energyLabels: entity.energyLabels,
        productVariants: entity.productVariants,
        boxConditionMinAgeEnabled: entity.boxConditionMinAgeEnabled,
        boxConditionMinAgeRequired: entity.boxConditionMinAgeRequired,
        boxConditionPartnerEmailEnabled: entity.boxConditionPartnerEmailEnabled,
        boxConditionPartnerEmailRequired:
            entity.boxConditionPartnerEmailRequired,
        boxHasContestRules: entity.boxHasContestRules,
        analytics: entity.analytics,
        salesIncentives: entity.salesIncentives,
        price: priceMapper(entity.price),
        limitedStock: entity.limitedStock === "YES",
        ecochequesAccepted: entity.ecochequesAccepted,
        clickAndCollectAvailable: isClickAndCollectAvailable(visibilityType),
        homeDeliveryAvailable: isHomeDeliveryAvailable(visibilityType),
        exclusivelyOnline:
            isHomeDeliveryAvailable(visibilityType) &&
            !isClickAndCollectAvailable(visibilityType),
        superCategory,
        visibilityType,
        visibleDirectLink: entity.visibleDirectLink === "YES",
    };

    return product;
};

export { productMapper };
